import { Box, Button, Flex, VStack } from '@chakra-ui/react';
import { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import { colors } from 'styles/colors';
import { B2bTermsAcceptanceCheckbox } from '../B2bTermsAcceptanceCheckbox/B2bTermsAcceptanceCheckbox';

/** Cancel + submit: fixed Figma width from `lg`; full-width stack on phones/tablets. */
const b2bRegistrationFooterCtaProps = {
  minW: '50.64px',
  h: '64px',
  minH: '64px',
  gap: '8px',
  py: '8px',
  px: '16px',
  borderRadius: '16px',
} as const;

export type B2bTermsAcceptancePanelProps = {
  checked: boolean;
  onChange: (checked: boolean) => void;
  isDisabled?: boolean;
  isRtlLayout?: boolean;
};

/** Full-width slot; styling lives on {@link B2bTermsAcceptanceCheckbox}. */
export const B2bTermsAcceptancePanel = ({
  checked,
  onChange,
  isDisabled = false,
  isRtlLayout = false,
}: B2bTermsAcceptancePanelProps) => (
  <Box w='full'>
    <B2bTermsAcceptanceCheckbox
      checked={checked}
      onChange={onChange}
      isDisabled={isDisabled}
      isRtlLayout={isRtlLayout}
    />
  </Box>
);

export type B2bTermsAndActionsSectionProps = {
  termsAccepted: boolean;
  onTermsAcceptedChange: (checked: boolean) => void;
  /**
   * Single-page **submit** or step **next** — product wires the handler.
   * Disabled until `termsAccepted` (and when `isDisabled`).
   */
  onPrimaryClick: () => void;
  /** Primary CTA label, e.g. `<FormattedMessage id="…" />` or `"Submit"`. */
  primaryLabel: ReactNode;
  /** Optional back control — not gated by terms. */
  onBack?: () => void;
  /** Defaults to `b2b.registration.action.cancel` when `onBack` is set. */
  backLabel?: ReactNode;
  /** Disables terms checkbox and both buttons (e.g. request in flight). */
  isDisabled?: boolean;
  isPrimaryLoading?: boolean;
  /** Document direction for the block (`rtl` for Arabic/Kurdish registration). */
  dir?: 'ltr' | 'rtl';
  lang?: string;
  /**
   * When true, only the action row is rendered (terms panel is placed elsewhere,
   * e.g. inside the event information card).
   */
  omitTermsPanel?: boolean;
};

/**
 * **Terms UI gate + action row** — {@link B2bTermsAcceptanceCheckbox} and {@link FormActions}.
 * Terms stay client-only (not in `buildB2BRegistrationRequestBody`). No API calls here.
 */
export const B2bTermsAndActionsSection = ({
  termsAccepted,
  onTermsAcceptedChange,
  onPrimaryClick,
  primaryLabel,
  onBack,
  backLabel,
  isDisabled = false,
  isPrimaryLoading = false,
  dir = 'rtl',
  lang = 'ar',
  omitTermsPanel = false,
}: B2bTermsAndActionsSectionProps) => {
  const primaryDisabled = !termsAccepted || isDisabled;
  const isRtlLayout = dir === 'rtl';

  return (
    <VStack spacing={4} align='stretch' w='full' dir={dir} lang={lang}>
      {!omitTermsPanel ? (
        <B2bTermsAcceptancePanel
          checked={termsAccepted}
          onChange={onTermsAcceptedChange}
          isDisabled={isDisabled}
          isRtlLayout={isRtlLayout}
        />
      ) : null}

      <Box
        bg={colors.b2b.formFooterBandBg}
        borderRadius='xl'
        px={{ base: 3, sm: 4, lg: 5 }}
        py={{ base: 3, lg: 4 }}
        w='full'
        minW={0}
      >
        <Flex
          w='full'
          minW={0}
          direction={{ base: 'column', lg: 'row' }}
          flexWrap='nowrap'
          justify={{
            base: 'stretch',
            lg: onBack != null ? 'space-between' : 'flex-end',
          }}
          align={{ base: 'stretch', lg: 'center' }}
          gap={3}
          rowGap={3}
          dir={dir}
        >
          {onBack != null ? (
            <Button
              type='button'
              variant='b2bRegistrationCancelTextLarge'
              {...b2bRegistrationFooterCtaProps}
              w={{ base: 'full', lg: '131px' }}
              flexShrink={0}
              borderWidth='1px'
              borderStyle='solid'
              borderColor='#CCCCCC'
              opacity={1}
              bg='transparent'
              _hover={{
                borderColor: '#CCCCCC',
              }}
              _active={{
                borderColor: '#CCCCCC',
              }}
              onClick={onBack}
              isDisabled={isDisabled}
            >
              {backLabel ?? (
                <FormattedMessage
                  id='b2b.registration.action.cancel'
                  defaultMessage='Cancel request'
                />
              )}
            </Button>
          ) : null}
          <Button
            type='button'
            variant='b2bRegistrationPrimaryLarge'
            {...b2bRegistrationFooterCtaProps}
            w={{ base: 'full', lg: '131px' }}
            flexShrink={0}
            onClick={onPrimaryClick}
            isDisabled={primaryDisabled}
            isLoading={isPrimaryLoading}
          >
            {primaryLabel}
          </Button>
        </Flex>
      </Box>
    </VStack>
  );
};
